import React, { useMemo, useState, useEffect, useCallback } from "react";
import Customtable from "../helper/Customtable";
import Swal from "sweetalert2";
import { post } from "../helper/api_helper";
import CommonModal from "../components/CommonModal";

const Payments = () => {
  // ------------------------------
  // Dynamic Form Field Config
  // ------------------------------
  const formFields = useMemo(() => [
    { name: "Bill No.", label: "Bill No.", type: "select", required: true, col: 6,
      options: [
        { value: 0, label: "SVC-2024-441-South India Paper Mill-100000" },
        { value: 1, label: "SVC-2024-388-Chennai Steel Corp-100000" },
      ],
     },
    { name: "consignee_id_fk", label: "Consignee", type: "select", col: 6,
      options: []
     },
    { name: "payment_date", label: "Payment Date", type: "date", col: 6 },
    { name: "amount", label: "Amount (₹)", type: "text", col: 6 },
    { name: "payment_mode", label: "Payment Mode", type: "select", col: 6,
      options: [
        { value: "", label: "-Select-" },
        { value: "neft", label: "NEFT" },
        { value: "rtgs", label: "RTGS" },
        { value: "cheque", label: "Cheque" },
        { value: "cash", label: "Cash" },
        { value: "upi", label: "UPI" },
        { value: "other", label: "Other" },
      ]
     },
    { name: "reference_no", label: "Reference No.", type: "email", col: 6 },
    {
      name: "remarks",
      label: "Remarks",
      type: "textarea",
      col: 12,
    },
  ], []);

  // ------------------------------
  // Initial Form State
  // ------------------------------
  const initialFormState = useMemo(() => {
    return formFields.reduce((acc, field) => {
      acc[field.name] = "";
      return acc;
    }, { Payments_id: 0 });
  }, [formFields]);

  const [formData, setFormData] = useState(initialFormState);
  const [PaymentsList, setPaymentsList] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [showModal, setShowModal] = useState(false);

  // ------------------------------
  // Alert Helper
  // ------------------------------
  const showAlert = useCallback((icon, title, text, timer = 2000) => {
    Swal.fire({
      icon,
      title,
      text: text || "Something went wrong.",
      timer,
      showConfirmButton: false,
    });
  }, []);

  // ------------------------------
  // Fetch Data
  // ------------------------------
  const fetchData = useCallback(async () => {
    try {
      const response = await post("/finance/Payments/list");
      if (response.status === 200) {
        setPaymentsList(response.data);
      } else {
        showAlert("error", response.status, response.message);
      }
    } catch (err) {
      showAlert("error", "Unexpected Error", err.message);
    }
  }, [showAlert]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ------------------------------
  // Edit Handler (FIXED POSITION)
  // ------------------------------
  const handleEdit = useCallback((row) => {
    setFormData({ ...initialFormState, ...row });
    setShowModal(true);
  }, [initialFormState]);

  // ------------------------------
  // Table Columns
  // ------------------------------
  const columns = useMemo(
    () => [
      { header: "S.No", label_condition: "s_no", enableColumnFilter: false, enableSorting: false },
      { header: "Payment Date", accessorKey: "payment_date" },
      { header: "Consignee", accessorKey: "consignee" },
      { header: "Bill No.", accessorKey: "bill_no" },
      { header: "Amount (₹)", accessorKey: "amount" },
      { header: "Mode", accessorKey: "mode" },
      { header: "Reference No.", accessorKey: "reference_no" },
      { header: "Remarks", accessorKey: "remarks"},
    ],
    [handleEdit]
  );

  // ------------------------------
  // Input Change Handler
  // ------------------------------
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // ------------------------------
  // Close Modal
  // ------------------------------
  const handleClose = () => {
    setShowModal(false);
    setFormData(initialFormState);
  };

  // ------------------------------
  // Submit Form
  // ------------------------------
  const handleSubmit = async () => {
    for (let field of formFields.filter((f) => f.required)) {
      if (!formData[field.name]) {
        showAlert("error", "Oops...", `${field.label} is required`);
        return;
      }
    }

    try {
      const response = await post("/finance/Payments", formData);
      if (response.status === 200) {
        showAlert("success", "Saved", response.message);
        fetchData();
        handleClose();
      } else {
        showAlert("error", response.status, response.message);
      }
    } catch (err) {
      showAlert("error", "Unexpected Error", err.message);
    }
  };

  // ------------------------------
  // Render
  // ------------------------------
  return (
    <>
      <div className="page-header d-flex justify-content-between align-items-center">
        <div>
          <div className="page-header-title">Payment Receipts</div>
          <div className="page-header-sub">All payments received</div>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => setShowModal(true)}>
          New Payment
        </button>
      </div>

      <div className="card basic-data-table mt-3">
        <div className="card-header">
          <h5 className="card-title mb-0">Payments List</h5>
        </div>
        <div className="card-body">
          <Customtable
            table_title="Payments List"
            columns={columns}
            data={PaymentsList}
            isGlobalFilter={true}
            isPagination={true}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            isExportButton={true}
          />
        </div>
      </div>

      <CommonModal
        show={showModal}
        title="Record Payment"
        formFields={formFields}
        formData={formData}
        onChange={handleChange}
        onClose={handleClose}
        onSubmit={handleSubmit}
        submitText="Save"
      />
    </>
  );
};

export default Payments;